import { Linear, Timeline, TimelineMax, TweenLite } from "gsap";
import { Howler } from "howler";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import { Application } from "../Application";
import {Anchor} from "../core/Anchor";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array, Callback} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import * as VO from "../data/VO";
import {Keyboard} from "../gameobjects/Keyboard";
import {SplashPlayButton} from "../gameobjects/SplashPlayButton";
import {StartScreenLayout} from "../layout/StartScreenLayout";

// @ts-ignore
const AudioContext = window.AudioContext || window.webkitAudioContext;
const inGamesApp = /packageName=org\.pbskids\.gamesapp/.test(window.location.search);

export class StartScreen extends HLF.LoadScreen {

    public static readonly NAME = "StartScreen";

    public readonly bg = this.addChild(new PIXI.Graphics());
    public readonly cloudContainer = this.addChild(new PIXI.Container());
    public readonly cloud = array(() => this.cloudContainer.addChild(new Sprite()));
    public readonly earth = this.addChild(new Sprite());
    public readonly bee = this.addChild(new Sprite());
    public readonly ladybug = this.addChild(new Sprite());
    public readonly comp = this.addChild(new Sprite("SplashScreen-static", {x: 0, y: 0}));
    public readonly loaderEmpty = this.addChild(new Sprite()); // static
    public readonly loaderFull = this.addChild(new Sprite());
    public readonly buttonPlay = this.addChild(new SplashPlayButton(undefined, () => this.onClickPlay({playButton: true})));

    public loadingText?: PIXI.BitmapText;

    protected idleTimeline?: TimelineMax;

    protected idleStartTime?: number;
    protected loaderMask = this.addChild(new PIXI.Graphics());
    private _onLoaded?: Callback;
    private _isAnimatedIn = false;
    private _titleVODidPlay = false;
    private _titleVODidFinish: boolean = false;
    private _playButtonWasClicked: boolean = false;

    public init(pSize: PIXI.Point) {
        this.bg.beginFill(0xC8E2F4, 1);
        this.bg.drawRect(0, 0, 2, 2);
        this.bg.endFill();
        this.bg.width = pSize.x;
        this.bg.height = pSize.y;
        StartScreenLayout.call(this);

        this.loaderFull.position.x -= this.loaderFull.width * this.loaderFull.anchor.x;
        this.loaderFull.anchor.set(0, 0.5);

        this.loaderMask.beginFill(0xff0000, 1);
        this.loaderMask.drawRect(0, -this.loaderFull.height / 2, this.loaderFull.width, this.loaderFull.height);
        this.loaderMask.endFill();
        this.loaderMask.position.copyFrom(this.loaderFull.position);
        this.loaderMask.scale.x = 0;

        this.loaderFull.mask = this.loaderMask;

        this.loadingText = this.addChild(HLF.PixiUtils.makeText("Loading ...", "sesame", 36 * HLF.Application.instance.renderer.resolution, 0x24a4db));
        this.loadingText.align = "center";
        this.loadingText?.position.set((1024 / 2) + 10, this.loaderFull.y + 50);

        this.buttonPlay.visible = false;
        this.comp.scale.set(0.5);
        this.comp.position.set((1024 - 1664) / 2, 0);
        this.comp.alpha = 0;

        super.init(pSize);

        this._isAnimatedIn = true;
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1024) / 2, 0);
        this.bg.x = -(pSize.x - 1024) / 2;
        this.bg.width = pSize.x;
        this.bg.height = pSize.y;
    }

    public onLoadComplete(pCallback: () => void): void {
        this._onLoaded = pCallback;
        TweenLite.to(this.loaderMask, 0.25, {pixi: {
            scaleX: 1,
        }, ease: Linear.easeNone});
        this.showPlayButton();
    }

    public onLoadProgress(pLoader: PIXI.Loader | {progress: number}, pResource?: {name: string, url: string}, pLoaderIndex: number = 0): void {
        super.onLoadProgress(pLoader);

        let percent = pLoader.progress / 100 / 2;
        if (pLoaderIndex > 0) {
            percent += 0.5;
        }
        TweenLite.to(this.loaderMask, 0.25, {pixi: {
            scaleX: percent * 1,
        }, ease: Linear.easeNone});
    }

    private showPlayButton() {
        if (this._isAnimatedIn && this._onLoaded) {

            this.interactive = true;
            this.addListener(HLF.Input.Events.POINTER_TAP, () => this.onClickPlay({playButton: false}));

            this.comp.alphaTo(1, 0.5);
            this.loadingText!.visible = false;
            this.loaderFull.alphaTo(0, 0.25);
            this.loaderEmpty.alphaTo(0, 0.25);
            this.buttonPlay.visible = true;
            this.buttonPlay.moveYFrom(this._size.y + this.buttonPlay.height, 0.4, Ease.backOut, () => {

                const playTitleVO = () => {
                    this._titleVODidPlay = true;
                    HLF.Application.instance.voiceover.playVO(VO.TS01, () => {
                        this.buttonPlay.outlineFilter.enabled = true;
                        this.buttonPlay.pulse();
                        this._titleVODidFinish = true;
                        if (this._playButtonWasClicked) {
                            this._onLoaded?.();
                        }
                        else {
                            HLF.Application.instance.voiceover.playVO(VO.TS04, HLF.PlayMode.Append, () => {
                                this.buttonPlay.outlineFilter.enabled = false;
                                this.buttonPlay.stopPulse();
                                this.idleTimeline?.restart(true);
                            });
                        }
                    });
                };

                if (inGamesApp) {
                    playTitleVO();
                }
                else if (AudioContext) {
                    const audioContext = Howler.ctx;
                    if (audioContext.state === "running") {
                        playTitleVO();
                    }
                }
                else {
                    playTitleVO();
                }
                this.idleTimeline = new TimelineMax();
                this.idleTimeline.delay(8);
                this.idleTimeline.call(() => {
                    console.log("idle");
                    (window as any).dataLayer = (window as any).dataLayer || [];
                    (window as any).dataLayer.push({
                        event: "gameTimeoutPlay",
                        gameName: "Go Green",
                        screenName: "1 Title Screen",
                        language: "English",
                        timestamp: (Date.now() / 1000),
                    });
                    console.log({
                        event: "gameTimeoutPlay",
                        gameName: "Go Green",
                        screenName: "1 Title Screen",
                        language: "English",
                        timestamp: (Date.now() / 1000),
                    });
                    if (this.idleStartTime === undefined) {
                        this.idleStartTime = new Date().valueOf();
                    }

                    this.buttonPlay.outlineFilter.enabled = true;
                    this.buttonPlay.pulse();
                });
                this.idleTimeline.call(() => {
                    this.buttonPlay.outlineFilter.enabled = false;
                    this.buttonPlay.stopPulse();
                    this.idleTimeline?.restart(true);
                }, undefined, undefined, "+=1");
                this.idleTimeline?.restart(true);
            });
            Keyboard.add(this.buttonPlay);
        }
    }

    private onClickPlay(pData: {playButton: boolean}) {
        this.idleTimeline?.kill();
        this.idleTimeline?.clear();
        HLF.Application.instance.audio.play("buttonclick", 1, false, HLF.AudioCategory.SFX);
        this.interactiveChildren = false;
        this.interactive = false;
        Keyboard.enabled = false;
        this._playButtonWasClicked = true;

        this.buttonPlay.outlineFilter.enabled = false;
        this.buttonPlay.stopPulse();
        this.idleTimeline?.restart(true);

        (window as any).dataLayer = (window as any).dataLayer || [];
        if (this.idleStartTime !== undefined) {
            const idleEndTime = new Date().valueOf();
            const idleDuration = (idleEndTime - this.idleStartTime) / 1000;
            (window as any).dataLayer.push({
                event: "gameTOReEngage",
                gameName: "Go Green",
                screenName: "1 Title Screen",
                language: "English",
                timestamp: (Date.now() / 1000),
                duration: idleDuration,
            });
            console.log("gameTOReEngage", {
                duration: idleDuration,
                screenName: "1 Title Screen",
            });
            this.idleStartTime = undefined;
        }

        (window as any).dataLayer.push({
            event: "gamePlay",
            gameName: "Go Green",
            screenName: "1 Title Screen",
            buttonName: pData.playButton ? "PlayButton" : "NotPlayButton",
            language: "English",
            timestamp: (Date.now() / 1000),
        });

        Application.instance.startTime = new Date().valueOf();
        Application.instance.playthroughStartTime = new Date().valueOf();

        console.log("gamePlay", {
            buttonName: pData.playButton ? "PlayButton" : "NotPlayButton",
            screenName: "1 Title Screen",
        });

        if (this._titleVODidPlay && this._titleVODidFinish) {
            this.buttonPlay.moveYTo(this._size.y + this.buttonPlay.height, 0.2, Ease.sineIn, this._onLoaded);
        }
        else if (this._titleVODidPlay) {
            this.buttonPlay.moveYTo(this._size.y + this.buttonPlay.height, 0.2, Ease.sineIn);
        }
        else {
            HLF.Application.instance.voiceover.playVO(VO.TS01, () => {
                this.buttonPlay.moveYTo(this._size.y + this.buttonPlay.height, 0.2, Ease.sineIn, this._onLoaded);
            });
        }
    }
}
